import React, { useState } from "react";
import { Checkbox } from "@swordsweeper/ssi-ui-component-library";
import { Button } from "@swordsweeper/ssi-ui-component-library";
import { useFormHandlers } from "@swordsweeper/react-forms";
import { SearchFacets } from "@src/api/SearchApi";
import { distributorNameToSlug } from "@src/constants/Distributors";
import { useCurrentUserStore } from "@src/state/currentUserState";
import { DistributorConnectionStatuses } from "@src/components/widgets/AccountDistributorSettings/DistributorConnectionConstants";
import kebabCase from "lodash/kebabCase";
import startCase from "lodash/startCase";

export interface SearchResultsFilters {
  savingsGreatest?: boolean;
  savingsCashBack?: boolean;
  savingsLevelUp?: boolean;
  savingsRewards?: boolean;
  savingsThisWeeksSpecials?: boolean;
  availabilityInStock?: boolean;
  availabilityOnAllocation?: boolean;
  availabilityDropshipped?: boolean;
  availabilityBackorder?: boolean;
  availabilitySpecialOrder?: boolean;
  availabilityLowStock?: boolean;
  hasImages?: boolean;
  categories?: string[];
  manufacturers?: string[];
  ingredients?: string[];
  distributors?: string[];
}

interface SearchResultsPageFiltersProps {
  initialData: SearchResultsFilters;
  facets?: SearchFacets | null;
  onSubmit: (formData: SearchResultsFilters, saveAsDefault: boolean) => void;
}

export default function SearchResultsPageFilters({
  initialData,
  facets,
  onSubmit,
}: SearchResultsPageFiltersProps): React.JSX.Element {
  const { formData, handleUpdate } =
    useFormHandlers<SearchResultsFilters>(initialData);
  const [saveAsDefault, setSaveAsDefault] = useState<boolean>(false);
  const { distributorConnections, getDistributorConnection } =
    useCurrentUserStore();

  // Helper to toggle array filter values (for categories, manufacturers, distributors)
  const toggleArrayValue = (
    fieldName: keyof SearchResultsFilters,
    value: string,
  ) => {
    const currentArray = (formData[fieldName] as string[]) || [];

    const filterValue =
      fieldName === "distributors" ? distributorNameToSlug(value) : value;
    const newArray = currentArray.includes(filterValue)
      ? currentArray.filter((v) => v !== filterValue)
      : [...currentArray, filterValue];
    handleUpdate({ name: fieldName as string, value: newArray });
  };

  const renderCheckbox = (
    name: string,
    checked: boolean = false,
    onChange: (value: boolean) => void,
    label: React.ReactNode,
  ) => (
    <div id={`search-filter-${kebabCase(name)}`}>
      <Checkbox
        key={name}
        className="checkbox text-sm"
        name={name}
        checked={checked}
        onChange={onChange}
        label={label}
      />
    </div>
  );

  return (
    <>
      <div className="flex-row spaced-content">
        {/* Column 1: Categories */}
        {facets?.categories && facets.categories.length > 0 && (
          <div className="flex-column flex-1">
            <div className="text-sm font-weight-bold margin-bottom-sm">
              Categories
            </div>
            {facets.categories.map((bucket) =>
              renderCheckbox(
                `category-${bucket.key}`,
                (formData.categories || []).includes(bucket.key),
                () => toggleArrayValue("categories", bucket.key),
                <span className="text-sm">
                  {bucket.key} ({bucket.count})
                </span>,
              ),
            )}
          </div>
        )}

        {/* Column 2: Manufacturers */}
        {facets?.manufacturers && facets.manufacturers.length > 0 && (
          <div className="flex-column flex-1">
            <div className="text-sm font-weight-bold margin-bottom-sm">
              Manufacturers
            </div>
            {facets.manufacturers.map((bucket) =>
              renderCheckbox(
                `manufacturer-${bucket.key}`,
                (formData.manufacturers || []).includes(bucket.key),
                () => toggleArrayValue("manufacturers", bucket.key),
                <span className="text-sm">
                  {bucket.key} ({bucket.count})
                </span>,
              ),
            )}
          </div>
        )}

        {/* Column 3: Ingredients */}
        {facets?.ingredients && facets.ingredients.length > 0 && (
          <div className="flex-column flex-1">
            <div className="text-sm font-weight-bold margin-bottom-sm">
              Active Ingredients
            </div>
            {facets.ingredients.map((bucket) =>
              renderCheckbox(
                `ingredient-${bucket.key}`,
                (formData.ingredients || []).includes(bucket.key),
                () => toggleArrayValue("ingredients", bucket.key),
                <span className="text-sm">
                  {startCase(bucket.key)} ({bucket.count})
                </span>,
              ),
            )}
          </div>
        )}

        {/* Column 4: Distributors + Product Features */}
        {facets && (
          <div className="flex-column flex-1">
            {facets.distributors && facets.distributors.length > 0 && (
              <>
                <div className="text-sm font-weight-bold margin-bottom-sm">
                  Distributors
                </div>
                {facets.distributors
                  .filter((bucket) => {
                    // Filter out NOT_CONNECTED distributors
                    // Only filter if distributorConnections have been loaded (not null)
                    if (distributorConnections === null) {
                      // If connections haven't loaded yet, show all distributors (backward compatibility)
                      return true;
                    }
                    const distributorSlug = distributorNameToSlug(bucket.key);
                    const connection =
                      getDistributorConnection(distributorSlug);
                    // Only show distributors that are CONNECTED
                    // Filter out if no connection record (NOT_CONNECTED) or status is explicitly NOT_CONNECTED
                    return (
                      connection &&
                      connection.status !==
                        DistributorConnectionStatuses.NotConnected
                    );
                  })
                  .map((bucket) => {
                    const distributorSlug = distributorNameToSlug(bucket.key);
                    return renderCheckbox(
                      `distributor-${bucket.key}`,
                      (formData.distributors || []).includes(distributorSlug),
                      () => toggleArrayValue("distributors", bucket.key),
                      <span className="text-sm">
                        {bucket.key} ({bucket.count})
                      </span>,
                    );
                  })}
              </>
            )}

            <div className="text-sm font-weight-bold margin-bottom-sm margin-top-md">
              Product Features
            </div>
            {renderCheckbox(
              "hasImages",
              formData.hasImages,
              (value: boolean) => handleUpdate({ name: "hasImages", value }),
              <span className="text-sm">
                Has Images{" "}
                {facets?.hasImages && facets?.hasImages[0]?.count > 0
                  ? `(${facets?.hasImages[0].count})`
                  : null}
              </span>,
            )}
          </div>
        )}
      </div>
      <div className="flex-row spaced-content-lg margin-top-lg">
        <Button
          className="btn btn-primary btn-md"
          onClick={() => onSubmit(formData, saveAsDefault)}
        >
          Apply Filters
        </Button>

        <Checkbox
          className="checkbox text-sm"
          name="setAsDefaultFilter"
          checked={saveAsDefault}
          onChange={setSaveAsDefault}
          label={<span className="text-sm">Set as Default Filter</span>}
        />
      </div>
    </>
  );
}
